"use client";

import { useMemo } from "react";
import Link from "next/link";
import { ArrowRight, CalendarRange, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useEffectivePass } from "@/hooks/useEffectivePass";
import { usePlanDynamicState } from "@/hooks/usePlanDynamicState";
import { todayKstISO } from "@/lib/kst-date";
import {
  getStudyPlan,
  STUDY_PLAN_IDS,
  STUDY_PLAN_LABELS,
  type StudyPlanId,
} from "@/lib/study-plan";
import {
  buildScheduledTasks,
  computeMetrics,
  personalStudyPlan,
  type OnTrackStatus,
  type PlanDynamicState,
  type ScheduleMetrics,
} from "@/lib/study-plan-state";

/**
 * 학습 계획 온트랙(on-track) 진단 카드 — 읽기 전용 (JCG-2310).
 *
 * "계획대로 잘 따라가고 있는지"만 진단한다. 일정 조정·체크는 /my/schedule 가 담당하므로
 * 여기엔 체크박스·드래그·조정 버튼이 일절 없다. 우상단 "일정 관리 ›"만 /my/schedule 로 연결.
 *
 * ★ 게이트: Starter+ (isAdFree) 면 보인다. Premium 전용 진도 데이터 게이트
 *   (hasStudyProgressAccess)와 독립이라 StudyProgressPage 의 Premium 잠금 분기 "바깥"에서
 *   렌더되어, Starter 사용자도 자신의 온트랙 상태를 볼 수 있다(진도 데이터는 잠겨 있어도).
 *   isAdFree 미만이면 카드 자체를 렌더하지 않는다(null).
 *
 * 핵심 비주얼 = 듀얼 마커 진행 레일:
 *   - 채움 바 = doneTotal / trackableTasks (전체 진척)
 *   - 오늘 마커 ▲ = scheduledByToday / trackableTasks
 *   - 채움 < 오늘마커 → 밀림(gap 노출), 채움 > 오늘마커 → 앞섬.
 *
 * 정적 계획은 클라가 직접 import(study-plan-index.json), 동적 상태만 서버액션으로 받는다.
 */
export default function StudyPlanOnTrackCard() {
  const { isAdFree, isLoaded, isSignedIn } = useEffectivePass();
  // 동적 상태 1회 fetch (로그인 + Starter 이상에서만 의미). 게이트 미통과면 fetch 생략 —
  // 그 경우 컴포넌트가 곧바로 null 을 반환하므로 stateLoading 을 건드릴 필요가 없다.
  const {
    dynState,
    loading: stateLoading,
    loadError,
    activeExamSessionId,
  } = usePlanDynamicState(
    !!(isLoaded && isSignedIn && isAdFree),
    "온트랙 카드 동적 상태 로드 오류:",
  );

  // 게이트: 로드 전 / 비로그인 / Starter 미만 → 카드 미표시(null).
  // (잠금 안내는 대시보드 "오늘의 학습" 위젯이 담당하므로 여기선 조용히 숨긴다.)
  if (!isLoaded || !isSignedIn || !isAdFree) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarRange
              className="h-4 w-4 text-amber-500"
              aria-hidden="true"
            />
            학습 계획 진행 상태
          </CardTitle>
          <Link
            href="/my/schedule"
            className="flex shrink-0 items-center gap-0.5 rounded text-xs font-medium text-muted-foreground transition-colors hover:text-amber-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:text-amber-400"
          >
            일정 관리
            <ArrowRight className="h-3 w-3" aria-hidden="true" />
          </Link>
        </div>
      </CardHeader>
      <CardContent
        aria-live="polite"
        aria-busy={stateLoading || !dynState || !activeExamSessionId}
      >
        {loadError ? (
          <p role="alert" className="py-8 text-sm text-muted-foreground">
            {loadError}
          </p>
        ) : stateLoading || !dynState || !activeExamSessionId ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            진행 상태 불러오는 중…
          </div>
        ) : (
          <OnTrackBody
            dynState={dynState}
            examSessionId={activeExamSessionId}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ── 본문 분기 ────────────────────────────────────────────────────────────────

function OnTrackBody({
  dynState,
  examSessionId,
}: {
  dynState: Record<StudyPlanId, PlanDynamicState>;
  examSessionId: string;
}) {
  const todayISO = todayKstISO();

  // 구독 + 시작된(not-started 아님) 계획만 카드로. computeMetrics 가 not-started 를 가려낸다.
  const plans = useMemo(() => {
    return STUDY_PLAN_IDS.flatMap((id) => {
      const dyn = dynState[id];
      const currentPlan = getStudyPlan(id, examSessionId);
      const plan =
        currentPlan && dyn ? personalStudyPlan(currentPlan, dyn) : null;
      if (!plan || !dyn || !dyn.subscribed) return [];
      const scheduled = buildScheduledTasks(plan, dyn);
      const metrics = computeMetrics(plan, scheduled, dyn, todayISO);
      if (metrics.status === "not-started") return [];
      return [{ id, totalDays: plan.totalDays, metrics }];
    });
  }, [dynState, examSessionId, todayISO]);

  // 구독 0개(또는 모두 not-started) → 가벼운 안내 1줄.
  if (plans.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <span className="text-2xl" aria-hidden="true">
          🌱
        </span>
        <p className="text-sm text-muted-foreground">
          학습 계획을 시작하면 진행 상태를 추적해요.
        </p>
        <Link
          href="/my/schedule"
          className="inline-flex items-center gap-0.5 rounded text-xs font-semibold text-amber-600 underline-offset-2 transition-colors hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-amber-400"
        >
          학습 계획 시작하기
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {plans.map((p) => (
        <PlanRail
          key={p.id}
          planId={p.id}
          totalDays={p.totalDays}
          metrics={p.metrics}
        />
      ))}
    </div>
  );
}

// ── 계획별 레일 ──────────────────────────────────────────────────────────────

/** 라벨 칩: 이론=blue, 코딩=green 톤 (dark 페어 포함, DashboardTodaySchedule 과 동일 체계). */
const PLAN_CHIP: Record<StudyPlanId, string> = {
  "theory-4week":
    "bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300",
  "coding-6week":
    "bg-green-50 text-green-700 dark:bg-green-950/50 dark:text-green-300",
  "warrior-13day": "bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
};

/** clamp 0~1 비율 */
function ratio(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(1, Math.max(0, part / whole));
}

function PlanRail({
  planId,
  totalDays,
  metrics,
}: {
  planId: StudyPlanId;
  totalDays: number;
  metrics: ScheduleMetrics;
}) {
  const {
    status,
    todayDayNumber,
    trackableTasks,
    doneTotal,
    scheduledByToday,
    backlog,
    daysUntilExam,
    remainingTasks,
    requiredPacePerDay,
  } = metrics;

  // 채움과 오늘 마커 모두 전체 학습 항목 수를 기준으로 놓는다.
  // 일차 비율을 섞으면 하루별 항목 수에 따라 밀린 개수와 간격이 달라 보인다.
  const fillPct = ratio(doneTotal, trackableTasks) * 100;
  const todayPct = ratio(scheduledByToday, trackableTasks) * 100;

  // 시각적 gap: 채움이 오늘 마커보다 뒤처진 구간(밀림 신호).
  const behindGap = Math.max(0, todayPct - fillPct);

  // 필요 페이스(올림) + 빡셈 판정: 평균 페이스의 2배를 넘으면 경고색.
  const avgPace = totalDays > 0 ? trackableTasks / totalDays : 0;
  const pacePerDay = Math.ceil(requiredPacePerDay);
  const paceTight = avgPace > 0 && requiredPacePerDay > avgPace * 2;

  const pill = PILL[status];
  const isDone = status === "done";

  // Day n/총 라벨(시작 전이면 표시 생략).
  const dayLabel =
    todayDayNumber != null
      ? `Day ${Math.min(todayDayNumber, totalDays)}/${totalDays}`
      : null;

  const railLabel = `${STUDY_PLAN_LABELS[planId]} 진행률 ${Math.round(fillPct)}%${
    todayDayNumber != null ? `, 오늘 일정 위치 ${Math.round(todayPct)}%` : ""
  }`;

  return (
    <section className="space-y-2.5">
      {/* 헤더: 계획 칩 + Day n/총 + 상태 pill + D-day */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-[11px] font-bold",
            PLAN_CHIP[planId],
          )}
        >
          {STUDY_PLAN_LABELS[planId]}
        </span>
        {dayLabel && (
          <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
            {dayLabel}
          </span>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
              pill.className,
            )}
          >
            <span aria-hidden="true">{pill.emoji}</span>
            {pill.label(metrics)}
          </span>
          {daysUntilExam > 0 ? (
            <span className="text-[11px] font-bold text-muted-foreground tabular-nums">
              D-{daysUntilExam}
            </span>
          ) : (
            <span className="text-[11px] font-bold text-amber-600 tabular-nums dark:text-amber-400">
              D-DAY
            </span>
          )}
        </span>
      </div>

      {/* 듀얼 마커 진행 레일 */}
      <div className="pt-3.5">
        <div
          className="relative h-2.5 w-full rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={Math.round(fillPct)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={railLabel}
        >
          {/* 밀린 구간(채움~오늘마커): 옅은 amber 빗금 톤으로 "여기가 비었어요" 신호 */}
          {behindGap > 0.5 && !isDone && (
            <div
              className="absolute inset-y-0 rounded-full bg-amber-200/60 dark:bg-amber-500/15"
              style={{ left: `${fillPct}%`, width: `${behindGap}%` }}
              aria-hidden="true"
            />
          )}

          {/* 채움 바 = 전체 진척 */}
          <div
            className={cn(
              "absolute inset-y-0 left-0 rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none",
              isDone
                ? "bg-amber-500 dark:bg-amber-400"
                : status === "ahead"
                  ? "bg-blue-500 dark:bg-blue-400"
                  : "bg-amber-500 dark:bg-amber-400",
            )}
            style={{ width: `${fillPct}%` }}
            aria-hidden="true"
          />

          {/* 채움 끝 다이아몬드(내 위치) */}
          {fillPct > 0 && !isDone && (
            <span
              className="absolute top-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[1px] border border-background bg-amber-600 shadow-sm dark:bg-amber-300"
              style={{ left: `${fillPct}%` }}
              aria-hidden="true"
            />
          )}

          {/* 오늘 마커 ▲ — 트랙 아래쪽에서 위로 가리키는 삼각형 + 상단 라벨 */}
          {todayDayNumber != null && (
            <span
              className="absolute -top-3.5 z-20 -translate-x-1/2"
              style={{ left: `${todayPct}%` }}
              aria-hidden="true"
            >
              <span className="flex flex-col items-center">
                <span className="text-[9px] font-semibold leading-none text-foreground/70">
                  오늘
                </span>
                <span className="mt-0.5 block h-0 w-0 border-x-[4px] border-t-[5px] border-x-transparent border-t-foreground/70" />
              </span>
            </span>
          )}
        </div>
      </div>

      {/* 수치 줄: 오늘까지 예정(참고) + 전체 완료(오늘·내일 상관없이 카운트) */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-[11px]">
        <span className="text-muted-foreground">
          오늘까지 예정{" "}
          <span className="font-semibold text-foreground tabular-nums">
            {scheduledByToday}
          </span>
          개
        </span>
        <span className="text-muted-foreground tabular-nums">
          완료{" "}
          <span className="font-semibold text-foreground">{doneTotal}</span>/
          {trackableTasks}{" "}
          <span className="text-muted-foreground/80">
            ({Math.round(fillPct)}%)
          </span>
        </span>
      </div>

      {/* 페이스 줄: 남은 N개 · 시험까지 D일 → 하루 K개면 완주 */}
      <PaceLine
        isDone={isDone}
        remainingTasks={remainingTasks}
        daysUntilExam={daysUntilExam}
        pacePerDay={pacePerDay}
        paceTight={paceTight}
        backlog={backlog}
        status={status}
      />
    </section>
  );
}

// ── 상태 pill 매핑 ────────────────────────────────────────────────────────────

type PillSpec = {
  emoji: string;
  className: string;
  label: (m: ScheduleMetrics) => string;
};

/**
 * 상태별 pill. 의미 색(green/blue/amber/red)에는 반드시 텍스트 라벨을 동반(a11y).
 * dark 페어는 dark-mode-colors.md §3-4(`-900/30` 배경 ↔ `-300` 텍스트) 패턴 차용.
 */
const PILL: Record<OnTrackStatus, PillSpec> = {
  "on-track": {
    emoji: "🟢",
    className:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
    label: () => "순항 중",
  },
  ahead: {
    emoji: "🔵",
    className:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    label: () => "앞서가는 중",
  },
  "slightly-behind": {
    emoji: "🟡",
    className:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    label: (m) => `${m.backlog}개 밀렸어요`,
  },
  behind: {
    emoji: "🔴",
    className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
    label: (m) => `따라잡기 필요 (${m.backlog}개)`,
  },
  done: {
    emoji: "🎉",
    className:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    label: () => "완주했어요!",
  },
  // not-started 는 OnTrackBody 에서 걸러져 여기 도달하지 않지만 Record 완전성을 위해 채움.
  "not-started": {
    emoji: "⚪",
    className: "bg-muted text-muted-foreground",
    label: () => "시작 전",
  },
};

// ── 페이스 줄 ────────────────────────────────────────────────────────────────

function PaceLine({
  isDone,
  remainingTasks,
  daysUntilExam,
  pacePerDay,
  paceTight,
  backlog,
  status,
}: {
  isDone: boolean;
  remainingTasks: number;
  daysUntilExam: number;
  pacePerDay: number;
  paceTight: boolean;
  backlog: number;
  status: OnTrackStatus;
}) {
  if (isDone) {
    return (
      <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
        모든 일정을 끝냈어요. 시험장에서 만나요! 🥔
      </p>
    );
  }

  // 시험일 도달(daysUntilExam=0)인데 남은 task 가 있으면 "오늘까지" 표현.
  const examReached = daysUntilExam <= 0;

  return (
    <p
      className={cn(
        "rounded-lg px-3 py-2 text-xs",
        paceTight
          ? "bg-red-50 font-medium text-red-700 dark:bg-red-950/30 dark:text-red-300"
          : "bg-muted/60 text-muted-foreground",
      )}
    >
      남은{" "}
      <span className="font-semibold text-foreground tabular-nums">
        {remainingTasks}개
      </span>{" "}
      ·{" "}
      {examReached ? (
        <>시험일 도달 → 오늘 끝내면 완주</>
      ) : (
        <>
          시험까지{" "}
          <span className="font-semibold text-foreground tabular-nums">
            {daysUntilExam}일
          </span>{" "}
          → 하루{" "}
          <span
            className={cn(
              "font-semibold tabular-nums",
              paceTight ? "text-red-700 dark:text-red-300" : "text-foreground",
            )}
          >
            {pacePerDay}개
          </span>
          면 완주
          {paceTight ? (
            <span className="ml-1 font-semibold">· 페이스 빠듯해요</span>
          ) : (
            <span aria-hidden="true"> ✓</span>
          )}
        </>
      )}
      {/* 밀린 경우 따라잡기 보조 안내(behind 상태에서만, 과밀 방지) */}
      {status === "behind" && backlog > 0 && !examReached && (
        <span className="ml-1 text-muted-foreground">
          (밀린 {backlog}개 포함)
        </span>
      )}
    </p>
  );
}
